import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  CreditCard,
  DollarSign,
  ShieldAlert,
  TrendingUp,
  Wallet,
} from "lucide-react";

import { PageWrapper } from "~/components/page-wrapper";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";

export const Route = createFileRoute("/_authenticated/analytics")({
  component: AnalyticsPage,
});

/**
 * Analytics landing page
 *
 * We intentionally DO NOT expose the Express Dashboard here.
 * Express accounts have limited dashboard access and merchants should
 * use our embedded components instead for a consistent experience.
 *
 * This page guides users to the appropriate embedded component pages.
 */
function AnalyticsPage() {
  const { hasPermission, isLoading, hasEmployee } = useEmployeePermissions();
  const canViewIntegrations = hasPermission("viewIntegrations");

  // Loading state
  if (isLoading) {
    return (
      <PageWrapper
        description="View your payment analytics and financial data"
        title="Analytics & Reporting"
      >
        <Skeleton className="h-96 w-full" />
      </PageWrapper>
    );
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <PageWrapper
        description="View your payment analytics and financial data"
        title="Analytics & Reporting"
      >
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>No Employee Profile</AlertTitle>
          <AlertDescription>
            Your account is not linked to an employee profile. Contact your
            organization admin to set up your employee record with the
            appropriate permissions.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  // No permission to view analytics
  if (!canViewIntegrations) {
    return (
      <PageWrapper
        description="View your payment analytics and financial data"
        title="Analytics & Reporting"
      >
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to view analytics. Contact your manager to
            request access.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper
      description="View your payment analytics and financial data"
      title="Analytics & Reporting"
    >
      <div className="space-y-6">
        {/* Overview message */}
        <Card>
          <CardHeader>
            <div className="flex items-start gap-4">
              <div className="bg-primary/10 rounded-full p-3">
                <TrendingUp className="text-primary size-6" />
              </div>
              <div>
                <CardTitle>Payment Analytics</CardTitle>
                <CardDescription className="mt-1">
                  Access detailed payment information, payout history, and
                  account balances through the pages below.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Quick links to embedded component pages */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="hover:border-primary/50 transition-colors">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 rounded-lg p-2">
                  <CreditCard className="text-primary size-5" />
                </div>
                <CardTitle className="text-lg">Payments</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <CardDescription>
                View all payment transactions, refunds, and disputes. Manage
                refunds and respond to disputes directly.
              </CardDescription>
              <Button asChild className="w-full" variant="outline">
                <Link to="/payments">
                  View Payments
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:border-primary/50 transition-colors">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="bg-success/10 rounded-lg p-2">
                  <Wallet className="text-success size-5" />
                </div>
                <CardTitle className="text-lg">Payouts</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <CardDescription>
                Track your payout schedule and history. View pending,
                in-transit, and completed payouts.
              </CardDescription>
              <Button asChild className="w-full" variant="outline">
                <Link to="/payouts">
                  View Payouts
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:border-primary/50 transition-colors">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="bg-info/10 rounded-lg p-2">
                  <DollarSign className="text-info size-5" />
                </div>
                <CardTitle className="text-lg">Balances</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <CardDescription>
                Monitor your account balance, pending funds, and available
                balance for payouts.
              </CardDescription>
              <Button asChild className="w-full" variant="outline">
                <Link to="/balances">
                  View Balances
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Coming soon section */}
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <TrendingUp className="text-muted-foreground mx-auto mb-4 size-12 opacity-50" />
            <h3 className="mb-2 font-semibold text-balance">
              Advanced Analytics Coming Soon
            </h3>
            <p className="text-muted-foreground mx-auto max-w-md text-sm">
              We're building comprehensive analytics dashboards with trends,
              customer insights, and business intelligence. Stay tuned!
            </p>
          </CardContent>
        </Card>
      </div>
    </PageWrapper>
  );
}
